/**
 * Trial Extension Store
 * Client state for the "extend your free trial" offer: the single booking modal
 * (popup, banner, top bar and Overview card all open it) plus in-memory mirrors of
 * the per-project booked / banner-dismissed / popup-shown flags, so the offer keeps
 * working for the session even when browser storage is unavailable.
 */

import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { readBookedFromUrl, readProjectFromUrl } from "../lib/trialExtension";

const withKey = (map, key) => (key ? { ...map, [key]: true } : map);

export const createTrialExtensionState = (set) => ({
  isModalOpen: false,
  modalSource: null,
  // Project booked from the calendar in this session (drives the confirmation).
  justBookedProjectKey: null,
  // Captured once when the app boots: routing may drop the query string later.
  bookedFromUrl: readBookedFromUrl(),
  // Project the boot URL refers to (`?project=`); AuthProvider removes it later.
  bookedFromUrlProject: readProjectFromUrl(),
  bookedProjects: {},
  bannerDismissedProjects: {},
  popupHandledProjects: {},
  // Clock used for eligibility; refreshed every minute and on tab focus.
  now: Date.now(),

  setNow: (now) => set({ now: now ?? Date.now() }),
  consumeBookedFromUrl: () =>
    set({ bookedFromUrl: false, bookedFromUrlProject: null }),

  openBooking: (source) =>
    set({ isModalOpen: true, modalSource: source || null }),
  closeBooking: () => set({ isModalOpen: false }),
  markBooked: (projectKey, { fromCalendar = true } = {}) =>
    set((state) => ({
      bookedProjects: withKey(state.bookedProjects, projectKey),
      justBookedProjectKey: fromCalendar
        ? projectKey
        : state.justBookedProjectKey,
    })),
  dismissBanner: (projectKey) =>
    set((state) => ({
      bannerDismissedProjects: withKey(
        state.bannerDismissedProjects,
        projectKey,
      ),
    })),
  markPopupHandled: (projectKey) =>
    set((state) => ({
      popupHandledProjects: withKey(state.popupHandledProjects, projectKey),
    })),
});

export const useTrialExtensionStore = create(
  devtools(createTrialExtensionState, { name: "trial-extension-store" }),
);
